// src/pages/ProductList.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Container, Grid, Typography, Box,
  FormControl, InputLabel, Select, MenuItem,
  TextField, InputAdornment, Pagination, Paper, Button
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useSearchParams } from 'react-router-dom';
import axiosInstance from '../services/api';
import ProductCard from '../components/ProductCard';
import VehicleFilter from '../components/VehicleFilter';
import SeoHead from '../components/SeoHead';

const PAGE_SIZE = 24;
const trUp = (s) => (s || '').toLocaleUpperCase('tr-TR');

const ProductList = () => {
  const [products, setProducts] = useState([]);
  const [subTypes, setSubTypes] = useState([]); // {id, name, typeId, typeName}
  const [search, setSearch] = useState('');
  const [selType, setSelType] = useState('');
  const [selSubType, setSelSubType] = useState('');
  const [sortOption, setSortOption] = useState('newest');
  const [vehicleFilter, setVehicleFilter] = useState(null);
  const [page, setPage] = useState(1);

  const [searchParams] = useSearchParams();
  const subtypeParam = searchParams.get('subtype');
  const typeParam = searchParams.get('type');
  const qParam = searchParams.get('q');

  // Ürünleri çek (sadece araç filtresine bağlı; gerisi istemci tarafı)
  const fetchProducts = useCallback(async () => {
    try {
      const endpoint = vehicleFilter?.type === 'series'
        ? `/products/byvehicle/${vehicleFilter.id}`
        : '/products';
      const { data } = await axiosInstance.get(endpoint);
      setProducts(data);
    } catch (error) {
      console.error(error);
      setProducts([]);
    }
  }, [vehicleFilter]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  // Kategori hiyerarşisini bir kez çek
  useEffect(() => {
    axiosInstance.get('/productsubtypes')
      .then(({ data }) => setSubTypes(data.map(st => ({
        id: st.id,
        name: st.name,
        typeId: st.productTypeId || st.productTypeDto?.id,
        typeName: st.productTypeDto?.name || 'Diğer',
      }))))
      .catch(() => {});
  }, []);

  // Tipler ve seçili tipe ait alt tipler
  const types = useMemo(() => {
    const map = new Map();
    subTypes.forEach(st => { if (st.typeId && !map.has(st.typeId)) map.set(st.typeId, st.typeName); });
    return [...map.entries()].map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name, 'tr'));
  }, [subTypes]);

  const subTypesOfType = useMemo(
    () => subTypes.filter(st => st.typeId === selType).sort((a, b) => a.name.localeCompare(b.name, 'tr')),
    [subTypes, selType]
  );

  // URL ?subtype= / ?type= geldiyse kategori seçimini ön-ayarla
  useEffect(() => {
    if (subtypeParam && subTypes.length) {
      const st = subTypes.find(s => s.id === subtypeParam);
      if (st) { setSelType(st.typeId); setSelSubType(st.id); }
    } else if (typeParam) {
      setSelType(typeParam); setSelSubType('');
    }
  }, [subtypeParam, typeParam, subTypes]);

  // URL ?q= (TopBar/üst arama) geldiğinde arama kutusunu doldur
  useEffect(() => { setSearch(qParam || ''); }, [qParam]);

  // Filtre + sıralama
  const filtered = useMemo(() => {
    let list = products;
    const q = trUp(search).trim();
    if (q) list = list.filter(p => trUp(p.name).includes(q) || trUp(p.oemCode).includes(q));
    if (selSubType) list = list.filter(p => p.productSubTypeId === selSubType);
    else if (selType) list = list.filter(p => (p.productSubTypeDto?.productTypeId) === selType);
    list = [...list];
    if (sortOption === 'newest') list.sort((a, b) => new Date(b.createTime) - new Date(a.createTime));
    else if (sortOption === 'name') list.sort((a, b) => a.name.localeCompare(b.name, 'tr'));
    return list;
  }, [products, search, selType, selSubType, sortOption]);

  // Filtre değişince ilk sayfaya dön
  useEffect(() => { setPage(1); }, [search, selType, selSubType, sortOption, vehicleFilter]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handlePage = (_e, val) => {
    setPage(val);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const clearFilters = () => {
    setSearch(''); setSelType(''); setSelSubType('');
  };

  const hasFilters = search || selType || selSubType;

  return (
    <>
      <SeoHead
        title="Ürün Kataloğu"
        description="Renault ve Dacia araçlarına uygun yedek parçalar. Orijinal ve muadil parçalar stoğumuzda."
        keywords="renault yedek parça, dacia yedek parça, oto yedek parça"
      />
      <Container sx={{ py: 4 }}>
        <Typography variant="h4" textAlign="center" fontWeight={700} gutterBottom>
          Ürün Kataloğu
        </Typography>

        {/* Araç filtresi */}
        <VehicleFilter onFilter={(f) => setVehicleFilter(f)} />

        {/* Arama + kategori + sıralama */}
        <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Ürün adı veya OEM kodu ara…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              sx={{ flex: '1 1 240px', minWidth: 200 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
                ),
              }}
            />
            <FormControl size="small" sx={{ minWidth: 170 }}>
              <InputLabel>Kategori</InputLabel>
              <Select
                label="Kategori"
                value={selType}
                onChange={e => { setSelType(e.target.value); setSelSubType(''); }}
              >
                <MenuItem value="">Tümü</MenuItem>
                {types.map(t => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 170 }} disabled={!selType}>
              <InputLabel>Alt Kategori</InputLabel>
              <Select
                label="Alt Kategori"
                value={selSubType}
                onChange={e => setSelSubType(e.target.value)}
              >
                <MenuItem value="">Tümü</MenuItem>
                {subTypesOfType.map(st => <MenuItem key={st.id} value={st.id}>{st.name}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Sırala</InputLabel>
              <Select value={sortOption} onChange={e => setSortOption(e.target.value)} label="Sırala">
                <MenuItem value="newest">Yeni Eklenen</MenuItem>
                <MenuItem value="name">İsim (A-Z)</MenuItem>
              </Select>
            </FormControl>
            {hasFilters && (
              <Button variant="text" onClick={clearFilters}>Temizle</Button>
            )}
          </Box>
        </Paper>

        {/* Sonuç sayacı */}
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>{filtered.length}</strong> ürün bulundu
            {filtered.length > PAGE_SIZE && ` · Sayfa ${page}/${pageCount}`}
          </Typography>
        </Box>

        {filtered.length === 0 ? (
          <Typography variant="body1" textAlign="center" color="text.secondary" sx={{ py: 6 }}>
            Ürün bulunamadı.
          </Typography>
        ) : (
          <>
            <Grid container spacing={3}>
              {pageItems.map(product => (
                <Grid item xs={12} sm={6} md={4} key={product.id}>
                  <ProductCard product={product} />
                </Grid>
              ))}
            </Grid>

            {pageCount > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <Pagination
                  count={pageCount}
                  page={page}
                  onChange={handlePage}
                  color="primary"
                  shape="rounded"
                  siblingCount={1}
                  boundaryCount={1}
                />
              </Box>
            )}
          </>
        )}
      </Container>
    </>
  );
};

export default ProductList;
